import { useState, useEffect } from 'react';

import { useFlags } from 'launchdarkly-react-client-sdk';

import {
    ApplicationAccessType,
    CommunicationType,
    CreateLandingPageInput,
    CreateTemplateInput,
    isTemplate as isTemplateType,
    LandingPage,
    Template,
    UpdateLandingPageInput,
    UpdateTemplateInput,
} from 'domains/content/types';

export type Params = {
    applicationsAccess?: ApplicationAccessType[];
    beeJson: string;
    communicationType?: CommunicationType;
    content: object | Template | LandingPage;
    id: string;
    isNameTooShort: boolean;
    isSaveClicked: boolean;
    isTemplate: boolean;
    isTemplateLayout: boolean;
    isUpdate: boolean;
    name: string;
    onSave: (input: CreateLandingPageInput | CreateTemplateInput) => void;
    onUpdate: (input: UpdateLandingPageInput | UpdateTemplateInput) => void;
    parentFolderId?: number;
    status?: string;
    /** When false, create/update mutations are not triggered (e.g. missing `content.manage`). Default true. */
    isSaveEnabled?: boolean;
    /** Multilingual bee JSON map (language code → parsed beeJson object). Only present when ML flag is on. */
    beeMlJson?: Record<string, any>;
    /**
     * When ML is active and the user is on a non-en-US language, pass the en-US beeJson string here so
     * the primary `beeJson` DB column always stores the default-language content (backward compat).
     * Falls back to `beeJson` when not provided.
     */
    mainBeeJson?: string;
};

/**
 * We can not call the mutations from Bee's callbacks directly because when the callbacks
 * are invoked the state like templateName is set to the initial value when the Bee plugin
 * was first rendered (possibly a bug with our code or an issue with the Bee Plugin).
 *
 * Autosave updates the beeJson which will trigger a save/update at the interval from the config if there are unsaved changes.
 * Saving is disabled for layouts.
 * Changing the template name will immediately trigger a save/update.
 * Manually clicking the save button will trigger a save/update.
 */
const useBeeAutoSave = ({
    applicationsAccess,
    beeJson,
    communicationType,
    content,
    id,
    isNameTooShort,
    isSaveClicked,
    isTemplate,
    isTemplateLayout,
    isUpdate,
    name,
    onSave,
    onUpdate,
    parentFolderId,
    status,
    isSaveEnabled = true,
    beeMlJson,
    mainBeeJson,
}: Params) => {
    // We can update a template multiple times, but only want to create a template once.
    const [isCreated, setIsCreated] = useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState([]);
    const { contentDraft: hasContentDraft } = useFlags();

    const trackChange = (change: object) => setUnsavedChanges([...unsavedChanges, change]);

    useEffect(() => {
        const isNameValid = name && !isNameTooShort;
        const isBeeJsonChangesValid = !!beeJson && !!unsavedChanges.length;

        if (!isSaveEnabled) {
            return;
        }
        if (isTemplateLayout) {
            return;
        }
        if (!isNameValid) {
            return;
        }
        if (!isSaveClicked && !isBeeJsonChangesValid) {
            return;
        }

        const sendDetails = isTemplateType(content)
            ? {
                  ...(content.preHeader && { preHeader: content.preHeader }),
                  ...(content.senderProfileId && { senderProfileId: content.senderProfileId }),
                  ...(content.subjectLine && { subjectLine: content.subjectLine }),
              }
            : {};

        const assessmentDetails = isTemplateType(content)
            ? {
                  assessmentId: content.assessmentId,
                  assessmentName: content.assessmentName,
                  assessmentConsentRequired: content.assessmentConsentRequired ?? true,
              }
            : {};

        const mutationInput = {
            // Use mainBeeJson (en-US) for the primary column when ML is active; fall back to beeJson.
            beeJson: mainBeeJson ?? beeJson,
            ...(isTemplate ? { templateName: name } : { name }),
            ...(parentFolderId ? { parentFolderId } : {}),
            ...sendDetails,
            ...assessmentDetails,
            ...(communicationType ? { communicationType } : {}),
            ...(applicationsAccess && { applicationsAccess: applicationsAccess.join(',') }),
            ...(status && hasContentDraft ? { status } : {}),
            ...(beeMlJson && { beeMlJson }),
        };

        if (isUpdate) {
            onUpdate({ id, ...mutationInput });
        } else if (!isCreated) {
            onSave(mutationInput);
            setIsCreated(true);
        }

        setUnsavedChanges([]);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [beeJson, isSaveClicked, isTemplateLayout, name]);

    const resetChanges = () => setUnsavedChanges([]);

    return { trackChange, isDirty: Boolean(unsavedChanges.length), resetChanges };
};

export default useBeeAutoSave;
